import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useSectionContent } from '../hooks/useSiteContent';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useJsonLd from '../hooks/useJsonLd';
import { JOB_POSTING_DISPATCH_ONLY } from '../seo/schemaOrg';
import { ArrowRight, DollarSign, ClipboardList, ShieldAlert, Briefcase } from 'lucide-react';

const DISPATCH_DEFAULTS = {
  dispatch_services_hero_title: 'Dispatch-Only Services',
  dispatch_services_hero_subtitle:
    'For Owner-Operators running under their own authority.',
  dispatch_services_overview:
    'Dispatch-Only is for Owner-Operators who operate under their own USDOT / MC authority and want professional load coordination. We handle load booking, broker communication, rate negotiation, paperwork, and back-office support.',
  dispatch_services_fee_structure:
    'Fee: 8% of gross revenue or $400 per week per truck, as defined in your dispatch agreement.',
  dispatch_services_what_we_handle:
    'Load sourcing and booking \u00b7 Broker setup and communication \u00b7 Rate confirmations and BOL coordination \u00b7 Macropoint / tracking setup \u00b7 Detention and accessorial documentation support.',
  dispatch_services_requirements:
    'Active USDOT / MC authority, active operating authority status with FMCSA, current cargo and auto liability insurance with required limits, W-9, signed dispatch agreement.',
  dispatch_services_disclaimer:
    'No revenue is guaranteed. Lane availability, broker terms, and seasonal market conditions affect freight access.',
};

const Section = ({ Icon, title, body, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-3 mb-3">
      <Icon className="w-6 h-6 text-blue-900" />
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    <p className="text-gray-700 leading-relaxed">{body}</p>
  </div>
);

const DispatchServicesPage = () => {
  const { content } = useSectionContent('dispatch_services', DISPATCH_DEFAULTS);
  useDocumentTitle({
    title: 'Dispatch-Only Services | BlueLock Freights',
    description:
      'Dispatch-Only for Owner-Operators running their own authority. Fee: 8% of gross revenue or $400 per week per truck, per dispatch agreement.',
  });
  useJsonLd('job-dispatch-only', JOB_POSTING_DISPATCH_ONLY);

  return (
    <div className="min-h-screen bg-white" data-testid="dispatch-services-page">
      <PublicNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-xs font-medium uppercase tracking-wide text-blue-200 mb-3">
            Program
          </span>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
            data-testid="dispatch-services-hero-title"
          >
            {content.dispatch_services_hero_title}
          </h1>
          <p
            className="text-lg sm:text-xl text-blue-100 max-w-3xl"
            data-testid="dispatch-services-hero-subtitle"
          >
            {content.dispatch_services_hero_subtitle}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <Section
            Icon={ClipboardList}
            title="Program Overview"
            body={content.dispatch_services_overview}
            testid="dispatch-services-overview"
          />

          <div data-testid="dispatch-services-fee-structure">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="w-6 h-6 text-blue-900" />
              <h2 className="text-2xl font-bold text-gray-900">Fee Structure</h2>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
              <p className="text-lg font-medium text-blue-900">
                {content.dispatch_services_fee_structure}
              </p>
            </div>
          </div>

          <Section
            Icon={Briefcase}
            title="What We Handle"
            body={content.dispatch_services_what_we_handle}
            testid="dispatch-services-what-we-handle"
          />
          <Section
            Icon={ShieldAlert}
            title="Requirements"
            body={content.dispatch_services_requirements}
            testid="dispatch-services-requirements"
          />

          <div
            className="p-6 bg-gray-50 border border-gray-200 rounded-xl"
            data-testid="dispatch-services-disclaimer"
          >
            <p className="text-sm text-gray-600">{content.dispatch_services_disclaimer}</p>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              to="/apply"
              className="inline-flex items-center bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              data-testid="dispatch-services-cta-apply"
            >
              Apply for Dispatch-Only <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <Link
              to="/programs"
              className="inline-flex items-center text-blue-900 font-medium hover:text-blue-700 px-6 py-3"
              data-testid="dispatch-services-link-programs"
            >
              Compare programs
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default DispatchServicesPage;
